import { memo } from "react";
import { cn } from "@/lib/cn";
import type { Conversation } from "@/lib/chat-types";

/* ── Icons ────────────────────────────────────────────── */

function ReviewIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
      <path d="M10 9H8" />
    </svg>
  );
}

function QAIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function CollapseIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={cn("transition-transform", collapsed && "rotate-180")}>
      <path d="M11 17l-5-5 5-5" />
      <path d="M18 17l-5-5 5-5" />
    </svg>
  );
}

/* ── Props ─────────────────────────────────────────────── */

interface ChatSidebarProps {
  conversations: Conversation[];
  activeId: string | null;
  collapsed: boolean;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onToggleCollapse: () => void;
  onClose?: () => void;
}

const ConversationRow = memo(function ConversationRow({
  conversation,
  activeId,
  collapsed,
  onSelect,
}: {
  conversation: Conversation;
  activeId: string | null;
  collapsed: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <div
      className={cn(
        "group relative",
        collapsed ? "px-2 py-1" : "px-2 py-1"
      )}
    >
      <button
        onClick={() => onSelect(conversation.id)}
        className={cn(
          "flex w-full items-center gap-2.5 min-w-0 text-left transition-colors cursor-pointer",
          collapsed ? "justify-center px-2 py-3 rounded" : "px-3 py-2.5 rounded",
          conversation.id === activeId
            ? "bg-[var(--color-accent)]/10 text-[var(--color-fg)]"
            : "text-[var(--color-fg-secondary)] hover:bg-[var(--color-border)]/50"
        )}
        title={conversation.title}
      >
        <span className={cn(
          "shrink-0 flex items-center justify-center w-7 h-7 rounded",
          conversation.id === activeId
            ? "bg-[var(--color-accent)]/20 text-[var(--color-accent)]"
            : "bg-[var(--color-border)] text-[var(--color-fg-tertiary)]"
        )}>
          {conversation.type === "review" ? <ReviewIcon /> : <QAIcon />}
        </span>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate">{conversation.title}</div>
            <div className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] mt-0.5">
              {conversation.type === "review" ? "Review" : "Q&A"} · {conversation.messageCount} msgs
            </div>
          </div>
        )}
      </button>
    </div>
  );
});

/* ── Component ─────────────────────────────────────────── */

export function ChatSidebar({
  conversations,
  activeId,
  collapsed,
  onSelect,
  onNewChat,
  onToggleCollapse,
}: ChatSidebarProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className={cn(
        "flex items-center shrink-0 h-11 border-b border-[var(--color-border)]",
        collapsed ? "justify-center px-2" : "justify-between px-4"
      )}>
        {!collapsed && (
          <span className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase text-[var(--color-fg-tertiary)]">
            Conversations
          </span>
        )}
        <button
          onClick={onNewChat}
          className="flex items-center justify-center w-6 h-6 rounded text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] hover:bg-[var(--color-border)] transition-colors cursor-pointer"
          aria-label="New conversation"
        >
          <PlusIcon />
        </button>
      </div>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto py-1">
        {conversations.map((conv) => (
          <ConversationRow
            key={conv.id}
            conversation={conv}
            activeId={activeId}
            collapsed={collapsed}
            onSelect={onSelect}
          />
        ))}
      </div>

      {/* Collapse toggle — desktop only */}
      <div className="shrink-0 border-t border-[var(--color-border)] hidden md:flex">
        <button
          onClick={onToggleCollapse}
          className={cn(
            "flex items-center gap-2 w-full py-2.5 text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg)] hover:bg-[var(--color-border)]/50 transition-colors cursor-pointer",
            collapsed ? "justify-center px-2" : "px-4"
          )}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <CollapseIcon collapsed={collapsed} />
          {!collapsed && (
            <span className="text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase">
              Collapse
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
